import data from './button-radii.json';

/**
 * ES module wrapper around the shared button-radii JSON source-of-truth.
 *
 * Mirrors `./button-radii.cjs` so the Tailwind preset (CJS) and TypeScript
 * consumers (ESM — Button.tsx, useScaledButtonRadii, useResponsiveRem) read
 * the SAME radius table at build- and run-time.
 *
 * Also hosts the pure math helper `computeResponsiveRem` used by the
 * platform-split useResponsiveRem hook. Putting it here (rather than in the
 * hook file) lets the unit test be platform-agnostic — no `react-native`
 * mock required.
 */

export type ButtonSizeKey = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';

export interface ButtonRadiiPx {
  inner: number;
  outerTop: number;
  outerBottom: number;
}

export const BUTTON_RADII_PX = data.sizes as Record<ButtonSizeKey, ButtonRadiiPx>;
export const REM_BASE = data.remBase;

/**
 * Pure math for the mobile responsive-rem hook. Exposed for unit tests.
 *
 *   - Window width does NOT scale rem (2026-09-27, product-owner call on the
 *     iPad review: "buttons and type are oversized almost everywhere"). The
 *     old tablet ramp drew every token at up to 1.25× on iPad (1.0× under
 *     768pt, linear to 1.25× at 1024pt); iPad now uses the phone / web-desktop
 *     sizes, and iPad-specific sizes are chosen per surface where the design
 *     asks for them. `width` stays in the signature so callers and the hook
 *     keep their shape.
 *   - OS font scale ("Larger Text"): clamped to [1.0, 1.3].
 *   - Final result: clamped to [REM_BASE × 0.875, REM_BASE × 1.5] (a 16-base
 *     rem stays within [14, 24] px).
 */
export function computeResponsiveRem(_width: number, fontScale: number): number {
  const cappedFontScale = Math.min(Math.max(fontScale, 1), 1.3);

  const raw = REM_BASE * cappedFontScale;
  return Math.min(Math.max(raw, REM_BASE * 0.875), REM_BASE * 1.5);
}

/** px → rem string. Kept here for parity with `button-radii.cjs`. */
/**
 * The corner a button shows the world: its bottom outer radius (the bevel's
 * 2px side, the larger of the two). A spotlight drawn around a button follows
 * this, plus its own padding.
 */
export function buttonOuterRadiusPx(size: ButtonSizeKey): number {
  return BUTTON_RADII_PX[size].outerBottom;
}

export function pxToRem(px: number): string {
  return `${(px / REM_BASE).toFixed(4).replace(/\.?0+$/, '')}rem`;
}
